/**
 * Success Panel Component
 * Shows appointment confirmed message with details
 */

import { format } from 'date-fns';
import type { Provider, AppointmentSlot } from '@/types';

// Icons
const CalendarIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const InfoIcon = () => (
  <svg className="ww-w-5 ww-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UserIcon = () => (
  <svg className="ww-w-full ww-h-full ww-text-gray-400" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

interface SuccessPanelProps {
  provider: Provider;
  slot: AppointmentSlot;
  service: string;
  onClose: () => void;
}

export const SuccessPanel = ({
  provider,
  slot,
  service,
  onClose,
}: SuccessPanelProps) => {
  const startTime = new Date(slot.startTime);
  const endTime = new Date(slot.endTime);

  return (
    <div className="ww-bg-white ww-border ww-border-gray-200 ww-rounded-lg ww-overflow-hidden">
      {/* Success Header */}
      <div className="ww-py-8 ww-text-center">
        <div className="ww-relative ww-w-20 ww-h-20 ww-mx-auto ww-mb-4">
          <div className="ww-w-20 ww-h-20 ww-bg-blue-500 ww-rounded-full ww-flex ww-items-center ww-justify-center ww-relative">
            <svg className="ww-w-10 ww-h-10 ww-text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div className="ww-absolute ww-w-5 ww-h-5 ww-bg-white ww-rounded-full ww-flex ww-items-center ww-justify-center ww-bottom-1 ww-right-1">
              <svg className="ww-w-3 ww-h-3 ww-text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        </div>
        <h2 className="ww-text-2xl ww-font-bold ww-text-gray-900">
          Appointment Confirmed Successfully!
        </h2>
      </div>

      {/* Appointment Details */}
      <div className="ww-p-6">
        {/* Provider Info */}
        <div className="ww-flex ww-items-center ww-gap-3 ww-pb-4 ww-border-b ww-border-gray-100">
          <div className="ww-w-12 ww-h-12 ww-rounded-full ww-overflow-hidden ww-bg-gray-100 ww-flex-shrink-0">
            {provider.avatarUrl ? (
              <img
                src={provider.avatarUrl}
                alt={provider.name}
                className="ww-w-full ww-h-full ww-object-cover"
              />
            ) : (
              <div className="ww-w-full ww-h-full ww-flex ww-items-center ww-justify-center">
                <UserIcon />
              </div>
            )}
          </div>
          <div>
            <h3 className="ww-font-semibold ww-text-gray-900">{provider.name}</h3>
            <p className="ww-text-sm ww-text-gray-500">{provider.specialty}</p>
          </div>
        </div>

        {/* Details */}
        <div className="ww-py-4 ww-space-y-3 ww-border-b ww-border-gray-100">
          <div className="ww-flex ww-items-center ww-gap-3 ww-text-gray-700">
            <CalendarIcon />
            <span className="ww-text-sm">{format(startTime, 'EEEE, MMMM d')}</span>
          </div>
          <div className="ww-flex ww-items-center ww-gap-3 ww-text-gray-700">
            <ClockIcon />
            <span className="ww-text-sm">
              {format(startTime, 'h:mm a')} - {format(endTime, 'h:mm a')}
            </span>
          </div>
          <div className="ww-flex ww-items-center ww-gap-3 ww-text-gray-700">
            <ClipboardIcon />
            <span className="ww-text-sm">{service || slot.appointmentType} ({slot.duration} minutes)</span>
          </div>
        </div>

        {/* What's Next */}
        <div className="ww-py-4">
          <div className="ww-flex ww-items-start ww-gap-2 ww-mb-3">
            <InfoIcon />
            <h4 className="ww-font-medium ww-text-gray-900">What's Next?</h4>
          </div>
          <ul className="ww-ml-7 ww-space-y-1 ww-text-sm ww-text-gray-600">
            <li className="ww-flex ww-items-start ww-gap-2">
              <span className="ww-mt-1.5 ww-w-1 ww-h-1 ww-bg-gray-400 ww-rounded-full ww-flex-shrink-0" />
              Confirmation mail will be sent
            </li>
            <li className="ww-flex ww-items-start ww-gap-2">
              <span className="ww-mt-1.5 ww-w-1 ww-h-1 ww-bg-gray-400 ww-rounded-full ww-flex-shrink-0" />
              Reminder will be sent 24hrs before the appointment
            </li>
            <li className="ww-flex ww-items-start ww-gap-2">
              <span className="ww-mt-1.5 ww-w-1 ww-h-1 ww-bg-gray-400 ww-rounded-full ww-flex-shrink-0" />
              Arrive 15 mins earlier with your ID
            </li>
          </ul>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="ww-w-full ww-py-3 ww-mt-4 ww-border ww-border-gray-300 ww-rounded-lg ww-text-sm ww-font-medium ww-text-gray-700 hover:ww-bg-gray-50 ww-transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default SuccessPanel;
